import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { CompleteAccountModalContext, ModalContext, openPlaceAbsenteeBidModal } from '@/redux/modules/modal';
import { EventContext } from '@/redux/modules/analytics';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getBidderId, getUserIsInGoodStanding, isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { getBidIncrementsByItemId } from '@/redux/modules/bidIncrements/bidIncrements.selectors';
import { getBidLimitBalance, getTotalBidLimitBalance } from '@/redux/modules/bidLimit';
import { getCatalogIsInstantAutopay } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { getItemSummary } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import { getNextBidMinimum } from '@/utils/absenteeBids';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTrackPlaceBidPressedAnalytics } from '@/utils/amplitude/analytics';
import BidLimitExceededPopover from '@/components/Popovers/BidLimitExceededPopover';
import Button from '@liveauctioneers/caterwaul-components/lib/Button/Button';
import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import styled from 'styled-components';
import usePrevious from '@liveauctioneers/caterwaul-components/lib/hooks/usePrevious';

export type ItemBiddingAbsenteeExperimentProps = {
    catalogId: number;
    itemId: number;
};

const ItemBiddingAbsenteeExperiment = ({ catalogId, itemId }: ItemBiddingAbsenteeExperimentProps) => {
    const dispatch = useAppDispatch();

    // items only used for logging around bidLimit bug, see:
    // https://liveauctioneers.atlassian.net/secure/RapidBoard.jspa?rapidView=43&modal=detail&selectedIssue=BUGS-3021&assignee=5f6108909109170076ef420e
    const bidderId = useAppSelector(getBidderId);
    const totalCatalogBidLimitBalance = useAppSelector((state) => getTotalBidLimitBalance(state, catalogId));

    const userLoggedIn = useAppSelector(isUserLoggedIn);
    const {
        bidderMaxBid = 0,
        currency = 'USD',
        isCatalogOnly = false,
        isLocked = false,
        isPassed = false,
        isSold = false,
        isTimedPlusAuction = false,
        leadingBid = 0,
        sellerId = 0,
        startPrice = 0,
    } = useAppSelector((state) => getItemSummary(state, itemId));
    const accountInGoodStanding = useAppSelector(getUserIsInGoodStanding);
    const bidIncrements = useAppSelector((state) => getBidIncrementsByItemId(state, itemId));
    const bidLimitBalance = useAppSelector((state) => getBidLimitBalance(state, catalogId));
    const { bidLimit, blockedByAuctioneer, declined, suspended } = useAppSelector((state) =>
        getCatalogRegistration(state, catalogId)
    );

    const isInstantAutopay = useAppSelector((state) => getCatalogIsInstantAutopay(state, catalogId));
    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));
    const isUnsold = isLocked && !isPassed && !isSold && !isLotOpen;

    // need to be sure to set the bidAmount initially
    const [bidAmount, setBidAmount] = useState(
        getNextBidMinimum(bidIncrements, Math.max(bidderMaxBid, leadingBid), startPrice) || 0
    );

    const [stateItemId, setItemId] = useState(itemId || 0);
    const [newBidMinimum, setNewBidMinimum] = useState(0);
    const [overBidLimit, setOverBidLimit] = useState(false);

    const previousBidLimitBalance = usePrevious(bidLimitBalance);
    const previousItemId = usePrevious(stateItemId);
    const previousNewBidMinimum = usePrevious(newBidMinimum);

    const modalContext = useMemo<ModalContext>(
        () => ({
            eventContext: EventContext.placeBid,
            id: itemId,
            itemId,
            type: CompleteAccountModalContext.PlaceBid,
        }),
        [itemId]
    );

    // If the bid (or its environment) changes, update state values
    useEffect(() => {
        // if there is no bid limit then `To Infinity! and Beyond!`
        const balance = bidLimitBalance || Infinity;
        // We need to store the lowest bid option so we can update the state when
        // the bid options change

        const newBidMinimum = getNextBidMinimum(bidIncrements, Math.max(bidderMaxBid, leadingBid), startPrice) || 0;

        if (
            previousBidLimitBalance !== balance ||
            previousItemId !== itemId ||
            previousNewBidMinimum !== newBidMinimum
        ) {
            const overBidLimit = newBidMinimum > balance;
            setBidAmount(newBidMinimum);
            setItemId(itemId);
            setNewBidMinimum(newBidMinimum);
            setOverBidLimit(overBidLimit);
        }
    }, [
        bidIncrements,
        bidLimitBalance,
        bidderMaxBid,
        stateItemId,
        leadingBid,
        previousBidLimitBalance,
        previousItemId,
        previousNewBidMinimum,
        startPrice,
        itemId,
        bidderId,
        totalCatalogBidLimitBalance,
    ]);

    const trackPlaceBidButtonPressedAnalytics = useTrackPlaceBidPressedAnalytics(catalogId, itemId);

    const placeBid = useCallback(() => {
        trackPlaceBidButtonPressedAnalytics();
        dispatch(
            openPlaceAbsenteeBidModal({
                bidAmount,
                isInstantAutopay,
                itemId,
                modalContext,
            })
        );
    }, [bidAmount, dispatch, isInstantAutopay, itemId, modalContext, trackPlaceBidButtonPressedAnalytics]);

    const isStoppedFromBidding =
        !accountInGoodStanding || (userLoggedIn && (declined || blockedByAuctioneer || suspended || overBidLimit));

    return isCatalogOnly || isPassed || isSold || isUnsold ? null : (
        <Container>
            <LayoutWrapper>
                <CopyWrapper>
                    {overBidLimit && (
                        <BidLimitExceededContainer>
                            <BodySecondary color="critical">
                                <FormattedMessage
                                    id="disabled_would_exceed"
                                    values={{
                                        limit: (
                                            <FormattedCurrency
                                                key="disabled-would-exceed-limit"
                                                sourceCurrency={currency}
                                                value={bidLimit}
                                            />
                                        ),
                                    }}
                                />
                            </BodySecondary>
                            <BidLimitExceededPopover
                                catalogId={catalogId}
                                sellerId={sellerId}
                            />
                        </BidLimitExceededContainer>
                    )}
                </CopyWrapper>
                <Button
                    disabled={isStoppedFromBidding}
                    onClick={placeBid}
                    primary
                    red={isTimedPlusAuction}
                    testid="itemPagePlaceBidButton"
                >
                    <FormattedMessage id="place_bid" />
                </Button>
            </LayoutWrapper>
        </Container>
    );
};

export default ItemBiddingAbsenteeExperiment;

const LayoutWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        gap: 24px;
    }
`;

const CopyWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

const BidLimitExceededContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
    & > [data-testid='popover'] > div {
        display: flex;
        align-items: center;
    }
`;

const Container = styled.div`
    @media print {
        display: none;
    }

    > * {
        width: 100%;
    }
`;
